import { useQuery } from "@tanstack/react-query";
import { APPROVALS, EMPLOYEES, OPEN_ROLES, VIRTUAL_ASSISTANTS } from "@/data/mock";
import { fetchClients } from "./clients";
import { withLatency } from "./simulate";

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return Math.round(values.reduce((s, v) => s + v, 0) / values.length);
}

async function computeOverview() {
  const clients = await fetchClients();
  const activeClients = clients.filter((c) => c.status === "active" || c.status === "at-risk");

  const avgHealth = average(activeClients.map((c) => c.healthScore)) ?? 0;
  const avgSatisfaction = average(activeClients.filter((c) => c.satisfactionScore != null).map((c) => c.satisfactionScore as number));
  const avgSla = average(activeClients.filter((c) => c.slaCompliance != null).map((c) => c.slaCompliance as number));

  const mrr = activeClients.reduce((sum, c) => sum + c.mrr, 0);

  const attritionRate = Number(
    ((EMPLOYEES.filter((e) => e.status === "offboarding").length / EMPLOYEES.length) * 100).toFixed(1),
  );

  const criticalClients = [...clients]
    .filter((c) => c.riskLevel === "critical" && c.status !== "churned")
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, 6);

  const overdueFollowUps = clients
    .filter((c): c is typeof c & { nextFollowUp: string } => Boolean(c.nextFollowUp) && new Date(c.nextFollowUp as string) < new Date())
    .sort((a, b) => new Date(a.nextFollowUp).getTime() - new Date(b.nextFollowUp).getTime())
    .slice(0, 6);

  const hiringBottlenecks = OPEN_ROLES.filter((r) => r.daysOpen > 45)
    .sort((a, b) => b.daysOpen - a.daysOpen)
    .slice(0, 5);

  const escalations = [...clients]
    .filter((c) => c.openIssues >= 3)
    .sort((a, b) => b.openIssues - a.openIssues)
    .slice(0, 5);

  const approvalsNeeded = APPROVALS.filter((a) => a.status === "pending").slice(0, 6);

  const recentWins = [...clients]
    .filter((c) => c.expansionOpportunity && c.healthScore > 85)
    .sort((a, b) => (b.expansionValue ?? 0) - (a.expansionValue ?? 0))
    .slice(0, 4);

  const recentLosses = [...clients]
    .filter((c) => c.status === "churned" || c.status === "at-risk")
    .sort((a, b) => b.mrr - a.mrr)
    .slice(0, 4);

  const leaderConcerns = Array.from(new Set(clients.filter((c) => c.riskLevel === "critical").map((c) => c.assignedTL)))
    .slice(0, 4)
    .map((name) => ({
      name,
      accountsAtRisk: clients.filter((c) => c.assignedTL === name && c.riskLevel === "critical").length,
    }));

  const aiRiskScore =
    avgSla != null ? Math.round(100 - avgHealth * 0.6 - avgSla * 0.4 + 10) : Math.round(100 - avgHealth + 10);

  return {
    kpis: {
      mrr,
      // No reliable live source for target/forecast deltas yet — these stay illustrative
      // until a real revenue-planning source is connected (see Revenue page for detail).
      monthlyTarget: Math.round(mrr * 1.06),
      newRevenue: null as number | null,
      expansionRevenue: null as number | null,
      lostRevenue: null as number | null,
      grossProfitPercent: 61.4,
      activeClients: activeClients.length,
      activeVAs: VIRTUAL_ASSISTANTS.length,
      openHiring: OPEN_ROLES.length,
      attritionPercent: attritionRate,
      healthScore: avgHealth,
      satisfactionScore: avgSatisfaction,
      slaCompliance: avgSla,
      aiRiskScore,
      mrrChangePercent: null as number | null,
    },
    attention: {
      criticalClients,
      overdueFollowUps,
      hiringBottlenecks,
      escalations,
      approvalsNeeded,
      recentWins,
      recentLosses,
      leaderConcerns,
    },
  };
}

export async function fetchOverview() {
  const data = await computeOverview();
  return withLatency(data, 100);
}

export function useOverview() {
  return useQuery({ queryKey: ["overview"], queryFn: fetchOverview, staleTime: 45_000 });
}
